import moment, {MomentInput} from 'moment';

export type ExportFileNameDateType = 'day' | 'day_hour';

type BuildExportFileNameOptions = {
  date?: ExportFileNameDateType;
  now?: MomentInput;
};

/**
 * Builds a file name for exported files by combining provided segments.
 *
 * @example
 * ```
 * buildExportFileName(['John Doe', 'Care Plan']);
 * // Output: 'John-Doe_Care-Plan'
 *
 * buildExportFileName(['John Doe', 'Care Plan'], {date: 'day'});
 * // Output: 'John-Doe_Care-Plan__2024-06-19'
 *
 * buildExportFileName(['John Doe', 'Care Plan'], {date: 'day_hour'});
 * // Output: 'John-Doe_Care-Plan_2024-06-19_14-30'
 * ```
 */
export function buildExportFileName(
  segments: Array<string | null | undefined>,
  options?: BuildExportFileNameOptions
): string {
  const normalizedSegments = segments
    .map((segment) => normalizeExportFileNameSegment(segment))
    .filter(Boolean);

  if (options?.date) {
    normalizedSegments.push(formatExportFileNameDate(options.date, options.now));
  }

  return normalizedSegments.join('_') || 'export';
}

function formatExportFileNameDate(dateType: ExportFileNameDateType, now?: MomentInput): string {
  return moment(now).format(dateType === 'day_hour' ? 'YYYY-MM-DD_HH-mm' : 'YYYY-MM-DD');
}

function normalizeExportFileNameSegment(segment?: string | null): string {
  return String(segment ?? '')
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/[_\s]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}
